import React, { createContext, useContext, useState, useEffect } from 'react';
import { getFirestore, collection, query, orderBy, limit, getDocs, where } from 'firebase/firestore';
import { performEnhancedCommunitySearch } from '../utils/chatgptSearch';

const CommunityContext = createContext();

// Helper function to check if a word matches another word (handles stems, partial matches)
const wordMatches = (searchWord, targetWord) => {
  if (!searchWord || !targetWord) return false;
  
  const searchLower = searchWord.toLowerCase();
  const targetLower = targetWord.toLowerCase();
  
  // Exact match
  if (targetLower === searchLower) return true;
  
  // Substring match
  if (targetLower.includes(searchLower) || searchLower.includes(targetLower)) return true;
  
  // Stem matching - check if search word is a stem of target word
  // e.g., "creator" matches "creating", "create", "creates", "created"
  const commonStems = [
    { stem: 'creat', matches: ['creat', 'creating', 'creator', 'creates', 'created', 'creation'] },
    { stem: 'design', matches: ['design', 'designing', 'designer', 'designs', 'designed'] },
    { stem: 'perform', matches: ['perform', 'performing', 'performer', 'performs', 'performed', 'performance'] },
    { stem: 'manage', matches: ['manage', 'managing', 'manager', 'manages', 'managed', 'management'] },
    { stem: 'produc', matches: ['produc', 'producing', 'producer', 'produces', 'produced', 'production'] },
    { stem: 'record', matches: ['record', 'recording', 'recorder', 'records', 'recorded'] },
    { stem: 'mix', matches: ['mix', 'mixing', 'mixer', 'mixes', 'mixed'] },
    { stem: 'sound', matches: ['sound', 'sounding', 'sound', 'sounds'] },
    { stem: 'light', matches: ['light', 'lighting', 'light', 'lights', 'lighted', 'lit'] },
    { stem: 'tech', matches: ['tech', 'technical', 'technician', 'technology'] },
  ];
  
  // Check if both words share a common stem
  for (const { stem, matches } of commonStems) {
    if (matches.some(m => searchLower.includes(m)) && matches.some(m => targetLower.includes(m))) {
      return true;
    }
  }
  
  // Check if search word is a prefix of target word (e.g., "creat" matches "creating")
  if (targetLower.startsWith(searchLower) && searchLower.length >= 4) return true;
  if (searchLower.startsWith(targetLower) && targetLower.length >= 4) return true;
  
  // Check for word boundaries - split target into words and check each
  const targetWords = targetLower.split(/[\s\-_]+/);
  for (const word of targetWords) {
    if (word.startsWith(searchLower) || searchLower.startsWith(word)) {
      if (Math.min(word.length, searchLower.length) >= 4) return true;
    }
  }
  
  return false;
};

// Frontend scoring system (works for both users and organizations)
export const calculateEntityScore = (entity, searchTerm) => {
  let score = 0;
  const details = [];

  // Users have firstName/lastName, organizations don't
  const firstName = (entity.firstName || '').toLowerCase();
  const lastName = (entity.lastName || '').toLowerCase();
  const username = (entity.username || '').toLowerCase();
  const displayName = (entity.displayName || `${entity.firstName || ''} ${entity.lastName || ''}`).toLowerCase();
  const userType = (entity.userType || '').toLowerCase();
  const bio = (entity.bio || '').toLowerCase();
  const location = (entity.location || '').toLowerCase();
  const title = (entity.title || '').toLowerCase();
  const accountType = (entity.accountType || '').toLowerCase();
  const postContent = (entity.postContent || '').toLowerCase();

  // Parse search terms - handle both comma-separated and space-separated
  const searchTerms = searchTerm
    .split(/[,\s]+/) // Split by commas and spaces
    .map(term => term.trim().toLowerCase())
    .filter(term => term.length > 0);

  // If no valid terms, return zero score
  if (searchTerms.length === 0) {
    return { total: 0, details: [] };
  }

  // Calculate score for each search term
  searchTerms.forEach(term => {
    let termScore = 0;
    const termDetails = [];

    // Split term into words for better matching
    const termWords = term.split(/\s+/).filter(w => w.length > 0);

    // Check each field for matches
    termWords.forEach(word => {
      // Exact matches (higher scores)
      if (firstName === word) {
        termScore += 10;
        termDetails.push('First name exact match');
      } else if (firstName.includes(word)) {
        termScore += 7;
        termDetails.push('First name partial match');
      } else if (wordMatches(word, firstName)) {
        termScore += 5;
        termDetails.push('First name stem match');
      }

      if (lastName === word) {
        termScore += 10;
        termDetails.push('Last name exact match');
      } else if (lastName.includes(word)) {
        termScore += 7;
        termDetails.push('Last name partial match');
      } else if (wordMatches(word, lastName)) {
        termScore += 5;
        termDetails.push('Last name stem match');
      }

      if (username === word) {
        termScore += 9;
        termDetails.push('Username exact match');
      } else if (username.includes(word)) {
        termScore += 6;
        termDetails.push('Username partial match');
      } else if (wordMatches(word, username)) {
        termScore += 4;
        termDetails.push('Username stem match');
      }

      // Display name support (full name)
      if (displayName.includes(word)) {
        termScore += 8;
        termDetails.push('Display name match');
      } else if (wordMatches(word, displayName)) {
        termScore += 5;
        termDetails.push('Display name stem match');
      }

      if (userType === word) {
        termScore += 8;
        termDetails.push('User type exact match');
      } else if (userType.includes(word)) {
        termScore += 5;
        termDetails.push('User type partial match');
      } else if (wordMatches(word, userType)) {
        termScore += 3;
        termDetails.push('User type stem match');
      }

      if (title === word) {
        termScore += 8;
        termDetails.push('Title exact match');
      } else if (title.includes(word)) {
        termScore += 5;
        termDetails.push('Title partial match');
      } else if (wordMatches(word, title)) {
        termScore += 3;
        termDetails.push('Title stem match');
      }

      if (accountType === word) {
        termScore += 8;
        termDetails.push('Account type exact match');
      } else if (accountType.includes(word)) {
        termScore += 5;
        termDetails.push('Account type partial match');
      } else if (wordMatches(word, accountType)) {
        termScore += 3;
        termDetails.push('Account type stem match');
      }

      if (location.includes(word)) {
        termScore += 4;
        termDetails.push('Location match');
      }

      // Bio matching - check for word matches in bio
      if (bio.includes(word)) {
        termScore += 3;
        termDetails.push('Bio keyword match');
      } else {
        // Check each word in bio for stem matches
        const bioWords = bio.split(/[\s\-_.,!?;:]+/).filter(w => w.length > 0);
        for (const bioWord of bioWords) {
          if (wordMatches(word, bioWord)) {
            termScore += 2;
            termDetails.push(`Bio stem match: "${word}" → "${bioWord}"`);
            break; // Only count once per search word
          }
        }
      }

      // Post content matching (for entityType === 'post')
      if (postContent.includes(word)) {
        termScore += 4;
        termDetails.push('Post content match');
      } else if (postContent) {
        const postWords = postContent.split(/[\s\-_.,!?;:]+/).filter(w => w.length > 0);
        for (const pw of postWords) {
          if (wordMatches(word, pw)) {
            termScore += 3;
            termDetails.push('Post content stem match');
            break;
          }
        }
      }
    });

    // Add term score to total
    score += termScore;
    details.push(...termDetails.map(detail => `${term}: ${detail}`));
  });

  // Bonus for matching multiple terms (comma-separated search)
  if (searchTerms.length > 1) {
    const matchedTerms = searchTerms.filter(term => {
      const termWords = term.split(/\s+/);
      return termWords.some(word => 
        firstName.includes(word) || wordMatches(word, firstName) ||
        lastName.includes(word) || wordMatches(word, lastName) ||
        username.includes(word) || wordMatches(word, username) ||
        userType.includes(word) || wordMatches(word, userType) ||
        title.includes(word) || wordMatches(word, title) ||
        accountType.includes(word) || wordMatches(word, accountType) ||
        location.includes(word) ||
        bio.includes(word) || bio.split(/[\s\-_.,!?;:]+/).some(bw => wordMatches(word, bw))
      );
    });

    if (matchedTerms.length > 1) {
      const bonus = (matchedTerms.length - 1) * 5; // 5 points per additional matched term
      score += bonus;
      details.push(`Multi-term bonus: +${bonus} (${matchedTerms.length}/${searchTerms.length} terms matched)`);
    }
  }

  return {
    total: score,
    details: details
  };
};

export const filterEntitiesBySearch = async (allEntities, searchTerm) => {
  if (!searchTerm || !searchTerm.trim()) {
    return {
      results: [],
      isEnhancedSearch: false
    };
  }

  const searchTermLower = searchTerm.trim().toLowerCase();

  // Special case: "All" displays all users and organizations
  if (searchTermLower === 'all') {
    const allEntitiesSorted = [...allEntities]
      .sort((a, b) => {
        const aTime = a.createdAt?.toDate?.() || a.createdAt || new Date(0);
        const bTime = b.createdAt?.toDate?.() || b.createdAt || new Date(0);
        return bTime - aTime;
      });
    
    return {
      results: allEntitiesSorted,
      isEnhancedSearch: false
    };
  }

  // Perform frontend scoring on all entities (don't filter yet - let ChatGPT analyze everything)
  const scoredResults = allEntities
    .map(entity => {
      const score = calculateEntityScore(entity, searchTermLower);
      return {
        ...entity,
        searchScore: score.total,
        matchDetails: score.details
      };
    })
    .filter(entity => entity.searchScore > 0)
    .sort((a, b) => b.searchScore - a.searchScore); // Sort by score

  // Always use ChatGPT for enhanced semantic analysis (not just for entities with bios)
  // ChatGPT can analyze usernames, titles, locations, bios, and all other fields
  console.log('🤖 Performing enhanced search with ChatGPT entities...');
  console.log('💬 scoredResults', scoredResults);
  
  try {
    // Pass ALL scored results to ChatGPT for semantic analysis
    // ChatGPT will analyze bios, usernames, titles, locations, etc. for semantic matches
    const enhancedResults = await performEnhancedCommunitySearch(searchTermLower, scoredResults);
    
    // Combine frontend scores with ChatGPT relevance scores
    // ChatGPT scores are weighted higher (multiply by 3) because they understand semantics
    const finalResults = enhancedResults.map(entity => {
      const chatGPTScore = (entity.relevanceScore || 0) * 100; // Convert 0-1 to 0-100 scale
      const frontendScore = entity.searchScore || 0;
      const finalScore = frontendScore + chatGPTScore * 3; // Weight ChatGPT 3x more
      
      return {
        ...entity,
        finalScore: finalScore,
        chatGPTScore: chatGPTScore,
        frontendScore: frontendScore
      };
    })
    .filter(entity => {
      // Include entities that have either:
      // 1. A ChatGPT relevance score > 0.1 (semantic match)
      // 2. A frontend score > 0 (exact/partial match)
      return (entity.relevanceScore && entity.relevanceScore > 0.1) || (entity.searchScore > 0);
    })
    .sort((a, b) => b.finalScore - a.finalScore);

    return {
      results: finalResults,
      isEnhancedSearch: true
    };
  } catch (error) {
    console.error('Enhanced search failed, using scored results:', error);
    // Fallback: filter and return frontend-scored results
    const filteredResults = scoredResults.filter(entity => 
      entity.searchScore > 0 ||
      ((entity.username || '').toLowerCase().includes(searchTermLower) ||
       (entity.firstName || '').toLowerCase().includes(searchTermLower) ||
       (entity.lastName || '').toLowerCase().includes(searchTermLower) ||
       (entity.bio || '').toLowerCase().includes(searchTermLower) ||
       (entity.title || '').toLowerCase().includes(searchTermLower) ||
       (entity.location || '').toLowerCase().includes(searchTermLower) ||
       (entity.postContent || '').toLowerCase().includes(searchTermLower))
    );
    
    return {
      results: filteredResults,
      isEnhancedSearch: false
    };
  }
};

export function CommunityProvider({ children }) {
  const [entities, setEntities] = useState([]); 
  const [allEntities, setAllEntities] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadAllEntitiesForSearch = async () => {
    try {
      const db = getFirestore();

      // Load community posts only (newest first) — users/orgs removed for now
      const postsQuery = query(
        collection(db, 'communityPosts'),
        orderBy('createdAt', 'desc'),
        limit(100)
      );
      const postsSnapshot = await getDocs(postsQuery);
      const allPosts = postsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        entityType: 'post',
      }));

      const profileTemplateQuery = query(
        collection(db, 'users'),
        where('profileCardTemplatePublic', '==', true),
        limit(100)
      );
      const profileTemplateSnapshot = await getDocs(profileTemplateQuery);
      const profileCards = profileTemplateSnapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((u) => u?.profileCardTemplate && Array.isArray(u.profileCardTemplate.items))
        .map((u) => ({
          id: `profile-card-${u.id}`,
          entityType: 'profileCard',
          userId: u.id,
          username: u.username || null,
          firstName: u.firstName || null,
          lastName: u.lastName || null,
          photoURL: u.photoURL || null,
          postContent: u.profileCardTemplate.postContent || '',
          location: u.profileCardTemplate.location || '',
          items: u.profileCardTemplate.items || [],
          cardBackground: u.profileCardTemplate.cardBackground || null,
          headerTextColor: u.profileCardTemplate.headerTextColor || null,
          interests: u.interests || [],
          showInterestsOnCard: u.profileCardTemplate.showInterestsOnCard !== false,
          createdAt:
            u.profileCardDirectoryListedAt ||
            u.profileCardTemplateMeta?.migratedAt ||
            u.updatedAt ||
            u.createdAt ||
            null,
        }));

      const combined = [...allPosts, ...profileCards].sort((a, b) => {
        const aTime = a.createdAt?.toDate?.() || (a.createdAt ? new Date(a.createdAt) : new Date(0));
        const bTime = b.createdAt?.toDate?.() || (b.createdAt ? new Date(b.createdAt) : new Date(0));
        return bTime - aTime;
      });
      setAllEntities(combined);
      console.log('💬 CommunityContext: Loaded posts', combined.length);
      return combined;
    } catch (error) {
      console.error('Error loading all entities:', error);
      return [];
    }
  };

  const loadEntities = async (isLoadingMore = false) => {
    try {
      if (!isLoadingMore) {
        setLoading(true);
        // Load all entities for search/filtering
        const allEntitiesData = await loadAllEntitiesForSearch();
        console.log('💬 CommunityContext: Loaded all entities', allEntitiesData.length);
        
        // Load first batch for display
        const firstBatch = allEntitiesData.slice(0, 20);
        setEntities(firstBatch);
        setHasMore(allEntitiesData.length > 20);
      } else {
        setLoadingMore(true);
        // Load more from the allEntities array
        const currentLength = entities.length;
        const nextBatch = allEntities.slice(currentLength, currentLength + 20);
        setEntities(prevEntities => [...prevEntities, ...nextBatch]);
        setHasMore(currentLength + 20 < allEntities.length);
      }
    } catch (error) {
      console.error('Error loading entities:', error);
      if (!isLoadingMore) {
        setEntities([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreEntities = () => {
    if (hasMore && !loadingMore) {
      loadEntities(true);
    }
  };

  const refreshEntities = () => {
    setHasMore(true);
    loadEntities();
  };

  useEffect(() => {
    loadEntities();
  }, []);
  

  return (
    <CommunityContext.Provider value={{ 
      entities, 
      allEntities, // Provide access to all entities for search/filter
      loading, 
      loadingMore,
      hasMore,
      refreshEntities,
      loadMoreEntities,
      filterEntitiesBySearch,
      calculateEntityScore,

    }}>
      {children}
    </CommunityContext.Provider>
  );
}

export const useCommunity = () => {
  const context = useContext(CommunityContext);
  if (!context) {
    throw new Error('useCommunity must be used within a CommunityProvider');
  }
  return context;
};

